import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'guard-dog:first-run:v1';

export interface FirstRunProgress {
  version: 1;
  started: boolean;
  connectorDownloaded: boolean;
  connectorActivated: boolean;
  tokenCopied: boolean;
  dismissed: boolean;
}

export const EMPTY_FIRST_RUN_PROGRESS: FirstRunProgress = {
  version: 1,
  started: false,
  connectorDownloaded: false,
  connectorActivated: false,
  tokenCopied: false,
  dismissed: false,
};

type FirstRunStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function firstRunStorageKey(tenantId: string): string {
  return `${STORAGE_PREFIX}:${tenantId}`;
}

function browserStorage(): FirstRunStorage | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** Read only the known boolean fields. Tokens and other unknown data are deliberately discarded. */
export function readFirstRunProgress(
  tenantId: string,
  storage: FirstRunStorage | undefined = browserStorage(),
): FirstRunProgress {
  if (!storage) return { ...EMPTY_FIRST_RUN_PROGRESS };
  try {
    const value = JSON.parse(storage.getItem(firstRunStorageKey(tenantId)) ?? 'null') as Record<
      string,
      unknown
    > | null;
    if (value?.version !== 1) return { ...EMPTY_FIRST_RUN_PROGRESS };
    return {
      version: 1,
      started: value.started === true,
      connectorDownloaded: value.connectorDownloaded === true,
      connectorActivated: value.connectorActivated === true,
      tokenCopied: value.tokenCopied === true,
      dismissed: value.dismissed === true,
    };
  } catch {
    return { ...EMPTY_FIRST_RUN_PROGRESS };
  }
}

export function writeFirstRunProgress(
  tenantId: string,
  progress: FirstRunProgress,
  storage: FirstRunStorage | undefined = browserStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(firstRunStorageKey(tenantId), JSON.stringify(progress));
  } catch {
    // Storage can be unavailable in private/locked-down browsers. The flow still works in-tab.
  }
}

/** Existing tenants do not enter first-run unless this browser previously saw the tenant empty. */
export function shouldShowFirstRun(siteCount: number, progress: FirstRunProgress): boolean {
  return siteCount === 0 || (progress.started && !progress.dismissed);
}

export function firstRunComplete(
  site: { lastSeenAt?: string } | undefined,
  inventoryCount: number,
): boolean {
  return Boolean(site?.lastSeenAt) && inventoryCount > 0;
}

/** Tenant-scoped, cross-tab browser progress. Server-observed site state remains authoritative. */
export function useFirstRunProgress(tenantId: string) {
  const [progress, setProgress] = useState<FirstRunProgress>(() => readFirstRunProgress(tenantId));

  useEffect(() => {
    setProgress(readFirstRunProgress(tenantId));
    const key = firstRunStorageKey(tenantId);
    const onStorage = (event: StorageEvent) => {
      if (event.key === key) setProgress(readFirstRunProgress(tenantId));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tenantId]);

  const update = useCallback(
    (patch: Partial<Omit<FirstRunProgress, 'version'>>) => {
      setProgress((current) => {
        const next = { ...current, ...patch, version: 1 as const };
        writeFirstRunProgress(tenantId, next);
        return next;
      });
    },
    [tenantId],
  );

  return { progress, update };
}
